import { Component, inject, OnInit, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';

import { ActivatedRoute, RouterLink } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { GenericService } from '../../../services/generic.services';
import { UtilitiesService } from '../../../services/utilities.service';

import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IPostSupportEmailRequest } from '@/app/models/contact-form.model';
import { catchError, of, tap } from 'rxjs';

@Component({
  selector: 'app-contact-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzGridModule,
    NzIconModule,
    NzImageModule,
    NzSkeletonModule,
    NzDividerModule,
    NzButtonModule,
    NzFormModule,
    NzResultModule,
    NzSelectModule,
    NzInputModule,
    NzMessageModule,
    NzRadioModule,
  ],
  templateUrl: './contact-form.component.html',
  styleUrls: ['./contact-form.component.scss'],
})
export class ContactFormComponent implements OnInit {
  sendingEmail: boolean = false;
  loadForm: boolean = true;
  loadMessage: boolean = false;
  validateForm!: UntypedFormGroup;

  urlIMG = environment.urlIMG;

  day: any = '';

  typeArray: Array<string> = [
    this.translateService.instant('FormBook.Claim'),
    this.translateService.instant('FormBook.Complaint'),
  ];
  typePrefix: Array<{ country: string; prefix: string }> =
    this.utilitiesService.getPrefixPhone();

  params: any;

  private http = inject(HttpClient);
  private env = environment;
  private headers = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  isErrorSupportEmail = signal<boolean>(false);

  constructor(
    private readonly _activateRoute: ActivatedRoute,
    private readonly translateService: TranslateService,
    private fb: UntypedFormBuilder,
    private utilitiesService: UtilitiesService,
    private message: NzMessageService,
    public genericServices: GenericService
  ) {}

  ngOnInit(): void {
    this.day = this.genericServices.getCurrentDate();
    this._activateRoute.params.subscribe((params: any) => {
      this.params = params.iosContact;
    });
    window.scrollTo(0, 0);
    this.validateForm = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.email, Validators.required]],
      emailConfirm: ['', [this.confirmValidator]],
      reason: [
        this.params
          ? this.translateService.instant('FormContact.RemoveAccountIos')
          : '',
        [Validators.required],
      ],
      description: ['', [Validators.required]],
    });
  }

  submitForm(): void {
    if (this.validateForm.valid) {
      this.sendingEmail = true;

      this.sendSupportEmail({
        description: this.validateForm.value?.description ?? '',
        email: this.validateForm.value?.email ?? '',
        name: this.validateForm.value?.name ?? '',
        reason: this.validateForm.value?.reason ?? '',
      });
    } else {
      this.message.create(
        'warning',
        this.translateService.instant('FormValid.Invalid')
      );
      Object.values(this.validateForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  validateConfirmEmail(): void {
    setTimeout(() =>
      this.validateForm.controls['emailConfirm'].updateValueAndValidity()
    );
  }

  confirmValidator = (
    control: UntypedFormControl
  ): { [s: string]: boolean } => {
    if (!control.value) {
      return { error: true, required: true };
    } else if (control.value !== this.validateForm.controls['email'].value) {
      return { confirm: true, error: true };
    }
    return {};
  };

  resetForm(): void {
    this.validateForm.controls['name'].setValue('');
    this.validateForm.controls['email'].setValue('');
    this.validateForm.controls['emailConfirm'].setValue('');
    this.validateForm.controls['reason'].setValue('');
    this.validateForm.controls['description'].setValue('');
    for (const key in this.validateForm.controls) {
      if (this.validateForm.controls.hasOwnProperty(key)) {
        this.validateForm.controls[key].markAsPristine();
        this.validateForm.controls[key].updateValueAndValidity();
      }
    }
  }

  sendSupportEmail(params: IPostSupportEmailRequest) {
    this.isErrorSupportEmail.set(false);

    const id = this.message.loading(
      this.translateService.instant('SendingEmail'),
      { nzDuration: 0 }
    ).messageId;

    this.http
      .post<IPostSupportEmailRequest>(
        `${this.env.baseUrlWeb}/support/email`,
        { ...params },
        {
          headers: this.headers,
        }
      )
      .pipe(
        tap(() => {
          this.sendingEmail = false;
        }),
        catchError((_) => {
          this.isErrorSupportEmail.set(true);
          setTimeout(() => {
            this.sendingEmail = false;
            this.loadForm = false;
            this.message.remove(id);
            this.message.create(
              'error',
              this.translateService.instant('FormValid.NotSendingEmail')
            );
          }, 1000);
          return of(null);
        })
      )
      .subscribe((data: any) => {
        if (data === 'Success') {
          setTimeout(() => {
            this.sendingEmail = false;
            this.loadForm = false;
            this.loadMessage = true;
            this.resetForm();
            this.message.remove(id);
          }, 1000);
        }
      });
  }
}
